import type { Client } from './index';
import { readSse } from './sse';

export type PlaylistCommand =
    | {
          action: 'player_switch';
          index: number;
          playlist_revision: string;
          command_id: string;
          expires_at: string;
      }
    | {
          action: 'playlist_pause' | 'playlist_start';
          playlist_revision: string;
          command_id: string;
          expires_at: string;
      };

// Keepalive comments arrive every 15 seconds; three missed ones mean the
// connection is dead even if the socket has not noticed yet.
const IDLE_TIMEOUT_MS = 45000;
const MAX_BACKOFF_MS = 30000;
const REMEMBERED_COMMANDS = 50;

function parseCommand(data: string): PlaylistCommand | null {
    try {
        const command = JSON.parse(data);
        if (
            typeof command?.command_id !== 'string' ||
            typeof command.playlist_revision !== 'string' ||
            typeof command.expires_at !== 'string'
        )
            return null;
        if (command.action === 'player_switch')
            return Number.isInteger(command.index) && command.index >= 0
                ? command
                : null;
        if (
            command.action === 'playlist_pause' ||
            command.action === 'playlist_start'
        )
            return command;
    } catch {
        /* Not a command. */
    }
    return null;
}

// Receives playlist commands from the runtime event stream while enabled.
// Commands are delivered at least once: duplicates and expired commands are
// dropped here, everything else is up to onCommand.
export class CommandStream {
    private client: Client;
    private onCommand: (command: PlaylistCommand) => void;
    private onDisabled: () => void;
    private enabled = false;
    private running = false;
    private controller: AbortController | null = null;
    private failures = 0;
    private retryAt = 0;
    private lastEventId: string | null = null;
    private seen: string[] = [];

    constructor(
        client: Client,
        onCommand: (command: PlaylistCommand) => void,
        // 409: the Playout no longer accepts commands. Reload the configuration,
        // which calls setEnabled() with the current sse_enabled again.
        onDisabled: () => void,
    ) {
        this.client = client;
        this.onCommand = onCommand;
        this.onDisabled = onDisabled;
    }

    setEnabled(enabled: boolean): void {
        if (!this.client.openCommandStream) enabled = false;
        this.enabled = enabled;
        if (!enabled) {
            this.controller?.abort();
            return;
        }
        if (!this.running) {
            this.run();
        }
    }

    private async run(): Promise<void> {
        this.running = true;
        try {
            while (this.enabled) {
                const wait = this.retryAt - Date.now();
                if (wait > 0) {
                    await new Promise((resolve) => setTimeout(resolve, wait));
                    if (!this.enabled) break;
                }

                if ((await this.connect()) === 'disabled') {
                    this.enabled = false;
                    this.onDisabled();
                }
                this.retryAt = Date.now() + this.backoff();
            }
        } finally {
            this.running = false;
        }
    }

    private backoff(): number {
        const delay = Math.min(1000 * 2 ** this.failures, MAX_BACKOFF_MS);
        this.failures++;
        // Jitter keeps many mixers from reconnecting in lockstep.
        return delay / 2 + (Math.random() * delay) / 2;
    }

    private async connect(): Promise<'closed' | 'disabled'> {
        const controller = new AbortController();
        this.controller = controller;
        let idle: ReturnType<typeof setTimeout> | null = null;
        const alive = () => {
            if (idle !== null) clearTimeout(idle);
            idle = setTimeout(() => controller.abort(), IDLE_TIMEOUT_MS);
        };

        try {
            alive();
            const body = await this.client.openCommandStream!(
                this.lastEventId,
                controller.signal,
            );
            console.log('playlist command stream connected');
            await readSse(
                body,
                (event) => {
                    if (event.event === 'playlist.command') {
                        this.handle(event.data);
                    }
                },
                () => {
                    this.failures = 0;
                    alive();
                },
            );
        } catch (error) {
            if ((error as { status?: number })?.status === 409) {
                console.warn('playlist commands are disabled for this playout');
                return 'disabled';
            }
            if (!controller.signal.aborted || this.enabled) {
                console.warn(`playlist command stream failed: ${error}`);
            }
        } finally {
            if (idle !== null) clearTimeout(idle);
            this.controller = null;
        }
        return 'closed';
    }

    private handle(data: string): void {
        const command = parseCommand(data);
        if (!command) {
            console.warn('ignoring malformed playlist command');
            return;
        }

        this.lastEventId = command.command_id;
        if (this.seen.includes(command.command_id)) return;
        this.seen.push(command.command_id);
        if (this.seen.length > REMEMBERED_COMMANDS) this.seen.shift();

        if (!(Date.parse(command.expires_at) > Date.now())) {
            console.warn(
                `ignoring expired playlist command ${command.command_id}`,
            );
            return;
        }

        this.onCommand(command);
    }
}
